import { Controller } from '@hotwired/stimulus';

const MIN_QUERY_LENGTH = 10;
const MAX_QUERY_LENGTH = 500;

export default class extends Controller {
  static targets = ['query', 'submit', 'error'];
  static values = { url: String };

  disconnect() {
    this.#abortInFlight();
  }

  async search(event) {
    event.preventDefault();

    const query = this.queryTarget.value.trim().replace(/\s+/g, ' ');
    if (!this.#validateQuery(query)) {
      return;
    }

    this.#clearError();
    this.#abortInFlight();
    this.#showLoading();

    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': this.#csrfToken,
        },
        body: JSON.stringify({ work: { work_description: query } }),
        signal: abortController.signal,
      });

      if (response.status === 429) {
        this.#showError("You've reached the limit for AI searches.");
        return;
      }

      const data = await response.json();

      if (data.redirect) {
        this.navigate(data.redirect);
        return;
      }

      if (!response.ok) {
        this.#showError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      this.#publishResults(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      this.#showError('Something went wrong. Please try again.');
    } finally {
      if (this.abortController === abortController) {
        this.abortController = null;
        this.#hideLoading();
      }
    }
  }

  navigate(url) {
    window.location.assign(url);
  }

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }

  #validateQuery(query) {
    if (query.length < MIN_QUERY_LENGTH) {
      this.#showError(`Please describe the problem in at least ${MIN_QUERY_LENGTH} characters.`);
      return false;
    }

    if (query.length > MAX_QUERY_LENGTH) {
      this.#showError(`Please keep your description to ${MAX_QUERY_LENGTH} characters or fewer.`);
      return false;
    }

    return true;
  }

  #publishResults({ html, query, work_item_ids: workItemIds, result_count: resultCount }) {
    window.Application = window.Application || {};
    window.Application.search_result_count = resultCount;
    window.Application.top_search_results = workItemIds;

    const currentState = window.History?.getState?.()?.data || {};
    const setState = currentState.search ? window.History.replaceState : window.History.pushState;
    const searchPath = `?ai-search=true&search=${this.#parameterize(query)}`;

    setState.call(
      window.History,
      {
        work_type: currentState.work_type,
        search: query,
        results: html,
      },
      document.title,
      searchPath,
    );

    // History.js skips statechange when the state is unchanged, which leaves
    // the results panel hidden after a repeat search for the same query.
    this.#revealResultsIfStillHidden();
  }

  #revealResultsIfStillHidden() {
    document.querySelector('#v2-search-results')?.classList.remove('hidden');
  }

  #parameterize(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  #showLoading() {
    document.querySelectorAll('.js-work-section').forEach((el) => el.classList.add('hidden'));
    document.querySelector('.loading-search-results')?.classList.remove('hidden');
    document.querySelector('#v2-search-results')?.classList.add('hidden');
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = true;
    }
  }

  #hideLoading() {
    document.querySelector('.loading-search-results')?.classList.add('hidden');
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false;
    }
  }

  #showError(message) {
    if (!this.hasErrorTarget) {
      return;
    }

    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove('hidden');
  }

  #clearError() {
    if (!this.hasErrorTarget) {
      return;
    }

    this.errorTarget.textContent = '';
    this.errorTarget.classList.add('hidden');
  }

  #abortInFlight() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}
